import { api } from '@/api/client';
import type {
  PaginationMeta,
  PersonalisationResult,
  PersonalisationValues,
  ProductCard,
  ProductDetail,
  ProductFilters,
  TaxonomyTerm,
} from '@/types/catalogue';

/**
 * Query keys for the catalogue.
 *
 * Filters are part of the products key, so changing a filter is a new cache
 * entry rather than a mutation of the current one — that is what lets
 * TanStack Query serve a previously-viewed filter combination instantly.
 */
export const catalogueKeys = {
  products: (filters: ProductFilters) => ['catalogue', 'products', filters] as const,
  product: (slug: string) => ['catalogue', 'product', slug] as const,
  related: (slug: string) => ['catalogue', 'product', slug, 'related'] as const,
  categories: ['catalogue', 'categories'] as const,
  occasions: ['catalogue', 'occasions'] as const,
  collections: ['catalogue', 'collections'] as const,
};

/**
 * Converts filters to query parameters.
 *
 * Empty arrays and undefined values are dropped rather than sent as empty
 * strings, which keeps URLs clean and — more importantly — keeps the query
 * key stable, so `{search: ''}` and `{}` do not become two cache entries for
 * the same result.
 */
function toParams(filters: ProductFilters): Record<string, unknown> {
  const params: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      if (value.length === 0) continue;
      params[key] = value.join(',');
      continue;
    }
    params[key] = value;
  }

  return params;
}

export interface ProductListResult {
  items: ProductCard[];
  meta: PaginationMeta;
}

/**
 * Paginated product listing.
 *
 * Unwraps `data` and `meta` from the envelope into one object so components
 * receive a single value and cannot render items without their pagination.
 */
export async function fetchProducts(filters: ProductFilters): Promise<ProductListResult> {
  const response = await api.getRaw<ProductCard[]>('/products', toParams(filters));

  return {
    items: response.data,
    meta: response.meta as unknown as PaginationMeta,
  };
}

export function fetchProduct(slug: string): Promise<ProductDetail> {
  return api.get<ProductDetail>(`/products/${slug}`);
}

export function fetchRelatedProducts(slug: string): Promise<ProductCard[]> {
  return api.get<ProductCard[]>(`/products/${slug}/related`);
}

/**
 * Asks the server to validate a personalisation payload — §10.
 *
 * Called before add-to-cart so problems surface while the customer is still
 * looking at the field. This is a preflight, not the enforcement point: the
 * cart validates again server-side, because a client can simply skip this.
 */
export function validatePersonalisation(
  slug: string,
  personalisation: PersonalisationValues,
): Promise<PersonalisationResult> {
  return api.post<PersonalisationResult>(
    `/products/${slug}/personalisation/validate`,
    { personalisation },
  );
}

export function fetchCategories(): Promise<TaxonomyTerm[]> {
  return api.get<TaxonomyTerm[]>('/categories');
}

export function fetchOccasions(): Promise<TaxonomyTerm[]> {
  return api.get<TaxonomyTerm[]>('/occasions');
}

export function fetchCollections(): Promise<TaxonomyTerm[]> {
  return api.get<TaxonomyTerm[]>('/collections');
}
